<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus } from 'lucide-svelte';
  import { tasksApi } from '$lib/api/tasks';
  import { membersApi } from '$lib/api/members';
  import { TASK_COLUMNS } from '$lib/types';
  import TaskCard from './TaskCard.svelte';
  import TaskModal from './TaskModal.svelte';
  import type { Project, Task } from '$lib/types';

  const { project }: { project: Project } = $props();
  const queryClient = useQueryClient();

  const tasksQuery = createQuery(() => ({
    queryKey: ['project-tasks', project.id],
    queryFn: () => tasksApi.forProject(project.id)
  }));
  const membersQuery = createQuery(() => ({
    queryKey: ['project-members', project.id],
    queryFn: () => membersApi.forProject(project.id)
  }));
  const tasks = $derived(tasksQuery.data?.results ?? []);
  const members = $derived(membersQuery.data?.results ?? []);

  let dragTask = $state<number | null>(null);
  let dragOverCol = $state<string | null>(null);

  const moveMutation = createMutation(() => ({
    mutationFn: ({ id, col }: { id: number; col: string }) => tasksApi.update(id, { col }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-tasks', project.id] })
  }));

  function onDragStart(e: DragEvent, taskId: number) {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try {
        e.dataTransfer.setData('text/plain', String(taskId));
      } catch {
        /* some browsers restrict setData outside a real drag gesture (e.g. tests) */
      }
    }
    dragTask = taskId;
  }
  function onDragEnd() {
    dragTask = null;
    dragOverCol = null;
  }
  function onColDragOver(e: DragEvent, colId: string) {
    e.preventDefault();
    if (dragOverCol !== colId) dragOverCol = colId;
  }
  function onColDragLeave(colId: string) {
    if (dragOverCol === colId) dragOverCol = null;
  }
  function onColDrop(e: DragEvent, colId: string) {
    e.preventDefault();
    const id = dragTask;
    dragTask = null;
    dragOverCol = null;
    if (id === null) return;
    const t = tasks.find((x) => x.id === id);
    if (!t || t.col === colId) return; // no-op on same column
    moveMutation.mutate({ id, col: colId });
  }

  let modalOpen = $state(false);
  let editingTask = $state<Task | null>(null);
  let createCol = $state<string>('backlog');
  function openCreate(colId: string) {
    editingTask = null;
    createCol = colId;
    modalOpen = true;
  }
  function openEdit(t: Task) {
    editingTask = t;
    modalOpen = true;
  }
</script>

<div class="kboard-scroll">
  <div class="kanban">
    {#each TASK_COLUMNS as c (c.id)}
      {@const items = tasks.filter((t) => t.col === c.id)}
      {@const limit = project.wip?.[c.id as 'in-progress' | 'review']}
      {@const over = !!limit && items.length > limit}
      <div
        class="col {dragOverCol === c.id ? 'drop' : ''}"
        role="group"
        aria-label="{c.name} column"
        ondragover={(e) => onColDragOver(e, c.id)}
        ondragleave={() => onColDragLeave(c.id)}
        ondrop={(e) => onColDrop(e, c.id)}
      >
        <div class="col-h">
          <span class="sw" style="background:{c.sw}"></span>
          {c.name}
          <span class="col-count {over ? 'over' : ''}" title={limit ? `WIP limit ${limit}` : ''}>
            {limit ? `${items.length} / ${limit}` : items.length}
          </span>
        </div>
        <div class="col-body">
          {#each items as t (t.id)}
            <TaskCard
              task={t}
              dragging={dragTask === t.id}
              {members}
              onDragStart={(e) => onDragStart(e, t.id)}
              {onDragEnd}
              onClick={() => openEdit(t)}
            />
          {/each}
          <button type="button" class="col-add" onclick={() => openCreate(c.id)}>
            <Plus size={15} />Add task
          </button>
        </div>
      </div>
    {/each}
  </div>
</div>

{#if modalOpen}
  <TaskModal projectId={project.id} task={editingTask} defaultCol={createCol} {members} onClose={() => (modalOpen = false)} />
{/if}
